import { Button } from "@bcc/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@bcc/components/ui/card";
import { Separator } from "@bcc/components/ui/separator";
import { useToast } from "@bcc/hooks/use-toast";
import { formatCurrency } from "@client-shared/utils";
import type { PurchaseOrderInvoice } from "@shared/types/payout";
import { BadgePercent, ChevronDown, ChevronUp, Coffee, FileSpreadsheet, Receipt, ShoppingCart } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { setTaxReportPanelTestId } from "./tax-report-panel.utils";
import { calculateTaxStats, exportTaxReportStats } from "./utils/tax-report";

interface TaxReportPanelProps {
  invoices: PurchaseOrderInvoice[];
  showKaffeesteuer: boolean;
  testId?: string;
}

interface StatCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  testId: string;
}

function StatCard({ label, value, icon, iconBg, iconColor, testId }: Readonly<StatCardProps>) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{label}</p>
            <p className="mt-2 text-3xl font-bold tracking-tight">{value}</p>
          </div>
          <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${iconBg}`}>
            <span className={iconColor}>{icon}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function TaxReportPanel({ invoices, showKaffeesteuer, testId }: Readonly<TaxReportPanelProps>) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);

  const { hasPreTaxData, hasVatData, hasKsData, totalOrders, totalPreTaxRevenue, currencyCode, totalVatCollected, totalKsCollected } =
    calculateTaxStats(invoices);

  const formatPreTaxRevenue = (amount: number) => (hasPreTaxData ? formatCurrency(amount, currencyCode) : "—");
  const formatVatCollected = (amount: number) => (hasVatData ? formatCurrency(amount, currencyCode) : "—");
  const formatKsCollected = (amount: number) => (hasKsData ? formatCurrency(amount, currencyCode) : "—");

  const handleExportCsv = () => {
    try {
      exportTaxReportStats({ invoices, showKaffeesteuer, t });
      toast({
        title: t("views.financeInvoices.toasts.exportStarted"),
        description: t("views.financeInvoices.toasts.exportingTaxReport"),
      });
    } catch {
      toast({
        title: t("views.financeInvoices.toasts.exportFailed"),
        variant: "destructive",
      });
    }
  };

  return (
    <div data-testid={testId ?? setTaxReportPanelTestId("container")}>
      <Button
        variant="ghost"
        onClick={() => setIsOpen((prev) => !prev)}
        data-testid={setTaxReportPanelTestId("toggle")}
        className="flex items-center gap-2 px-0 font-semibold text-foreground hover:bg-transparent"
        aria-expanded={isOpen}
      >
        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        {isOpen ? t("views.financeInvoices.taxReport.hide") : t("views.financeInvoices.taxReport.show")}
      </Button>

      {isOpen && (
        <Card className="mt-3" data-testid={setTaxReportPanelTestId("panel")}>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold">{t("views.financeInvoices.taxReport.title")}</CardTitle>
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">{t("views.financeInvoices.taxReport.subtitle")}</span>
                {invoices.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={handleExportCsv}
                    data-testid={setTaxReportPanelTestId("export-csv-button")}
                    className="gap-2"
                  >
                    <FileSpreadsheet className="h-4 w-4" />
                    {t("views.financeInvoices.exportCsv")}
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <Separator />
          <CardContent className="pt-4">
            <div className={showKaffeesteuer ? "grid gap-4 md:grid-cols-4" : "grid gap-4 md:grid-cols-3"}>
              <StatCard
                label={t("views.financeInvoices.taxReport.totalOrders")}
                value={String(totalOrders)}
                icon={<ShoppingCart className="h-6 w-6" />}
                iconBg="bg-blue-100"
                iconColor="text-blue-600"
                testId={setTaxReportPanelTestId("stat-total-orders")}
              />
              <StatCard
                label={t("views.financeInvoices.taxReport.totalPreTaxRevenue")}
                value={formatPreTaxRevenue(totalPreTaxRevenue)}
                icon={<Receipt className="h-6 w-6" />}
                iconBg="bg-green-100"
                iconColor="text-green-600"
                testId={setTaxReportPanelTestId("stat-pre-tax-revenue")}
              />
              <StatCard
                label={t("views.financeInvoices.taxReport.totalVatCollected")}
                value={formatVatCollected(totalVatCollected)}
                icon={<BadgePercent className="h-6 w-6" />}
                iconBg="bg-amber-100"
                iconColor="text-amber-600"
                testId={setTaxReportPanelTestId("stat-vat-collected")}
              />
              {showKaffeesteuer && (
                <StatCard
                  label={t("views.financeInvoices.taxReport.totalKsCollected")}
                  value={formatKsCollected(totalKsCollected)}
                  icon={<Coffee className="h-6 w-6" />}
                  iconBg="bg-orange-100"
                  iconColor="text-orange-600"
                  testId={setTaxReportPanelTestId("stat-ks-collected")}
                />
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
